import { z } from 'zod';
import { type Role, managedBySchema, otpPolicySchema, themeSchema } from '../enums';
import type { AiFeature } from '../enums/ai';
import {
  CALENDAR_LIMITS,
  calendarReminderMinutesSchema,
  feedPerformanceDetailSchema,
  freeBusyLeaveVisibilitySchema,
} from '../enums/calendar';
import { CALL_LIMITS, RECORDING_LIMITS } from '../enums/calls';
import { pushPreviewSchema, readReceiptsSettingSchema } from '../enums/notifications';
import { managerPayoutVisibilitySchema } from '../enums/pay';
import { DEFAULT_TIME_ZONE } from '../format';
import type { MemberGrant } from '../tasks';
import {
  DEFAULT_ACCENT_COLOR,
  EMAIL_DOMAIN_PATTERN,
  HEX_COLOR_PATTERN,
  isValidTimeZone,
} from '../validators';

/**
 * Workspace settings registry (D-048). `workspace_settings` stores one row per dotted key
 * (`<module>.<camelCaseName>`); every key must be registered here with a Zod schema and a
 * default, and a missing row means the default. Modules add their own keys as they are built.
 *
 * The schemas check *values*; business rules that answer with a specific 422 code (the accent
 * contrast, `invalid_timezone`) are checked by the service before writing, using
 * `@orbit/shared/validators`.
 */

const hhmm = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm (24 h)');

/** A positive number of working hours (task follow-up timers, D-056). */
const workingHours = z.number().positive().max(1000);

/** ISO weekdays (1 = Monday … 7 = Sunday), 1–7 unique entries, stored sorted. */
export const workingDaysSchema = z
  .array(z.number().int().min(1).max(7))
  .min(1)
  .max(7)
  .refine((days) => new Set(days).size === days.length, 'Weekdays must be unique');

/** Hostnames never unfurled (`messaging.unfurlBlockedDomains`): lowercase, at most 200. */
export const unfurlBlockedDomainsSchema = z
  .array(z.string().regex(EMAIL_DOMAIN_PATTERN, 'Use a lowercase hostname such as example.com'))
  .max(200);

/** Default N (hours) of the unread-mentions email digest (`notifications.emailDigestDefaultHours`). */
export const emailDigestDefaultHoursSchema = z.number().int().min(1).max(24);

/** Who manages a channel's incoming webhooks (`integrations.webhooksManagedBy`). */
export const webhooksManagedBySchema = z.enum(['admins', 'channel_members']);

/** Upload ceiling of a Slack export ZIP, 1 MiB to 200 GiB (`import.maxArchiveBytes`). */
export const importMaxArchiveBytesSchema = z
  .number()
  .int()
  .min(1024 * 1024)
  .max(200 * 1024 * 1024 * 1024);

export const emailDomainListSchema = z
  .array(z.string().regex(EMAIL_DOMAIN_PATTERN, 'Use a lowercase hostname such as comhard.co.in'))
  .max(20);

/** `a.b.c.d/n` (the service also checks that the range is inside RFC 1918). */
export const IPV4_CIDR_PATTERN =
  /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}\/(3[0-2]|[12]?\d)$/;

/** One entry of `auth.ssoProviders` (PLAN §3.1 workspace_settings, D-008). */
export const ssoProviderSettingSchema = z.strictObject({
  provider: z.enum(['google', 'microsoft']),
  /** Microsoft Entra tenant id (`tid`); required for `microsoft`. */
  tenantId: z.string().trim().min(1).max(64).optional(),
  /** Google Workspace hosted domains (`hd`) allowed to sign in; empty = any verified email. */
  hostedDomains: z.array(z.string().regex(EMAIL_DOMAIN_PATTERN)).max(20).default([]),
  /** Reserved: SSO never creates members in v1 (there is no just-in-time sign-up). */
  autoJoin: z.literal(false).default(false),
});
export type SsoProviderSetting = z.output<typeof ssoProviderSettingSchema>;

export const ssoProvidersSchema = z
  .array(ssoProviderSettingSchema)
  .max(2)
  .refine(
    (items) => new Set(items.map((item) => item.provider)).size === items.length,
    'Each provider at most once',
  )
  .refine(
    (items) => items.every((item) => item.provider !== 'microsoft' || item.tenantId),
    'Microsoft needs a tenantId',
  );

export const SETTINGS_REGISTRY = {
  /** Workspace timezone for all business-day maths (D-013). */
  'calendar.timezone': {
    schema: z.string().refine(isValidTimeZone, 'Unknown IANA time zone'),
    default: DEFAULT_TIME_ZONE,
  },
  /** Working days, Monday–Saturday by default (D-031). */
  'calendar.workingDays': { schema: workingDaysSchema, default: [1, 2, 3, 4, 5, 6] },
  /**
   * Default working hours (D-056), in each member's own zone (D-1025). Admins edit both in
   * `/admin/calendar` (wave 5): `calendar.updateSettings` checks them together (start before end,
   * at least 60 minutes apart, `422 invalid_workday_hours`). They also drive the working-time maths
   * of tasks and PTSTDM from the change on.
   */
  'calendar.workdayStart': { schema: hhmm, default: '09:30' },
  'calendar.workdayEnd': { schema: hhmm, default: '18:30' },
  /**
   * OTP at sign-in: `always`, `new_device` (default) or `off` (D-008).
   *
   * **INERT since D-1109.** Orbit holds no credentials and runs no second factor: sign-in is
   * `account.comsky.ai`, which owns passwords, second factors, session lifetime and revocation.
   * Nothing in this product reads this value to decide anything any more.
   *
   * It is kept, rather than deleted, so that rows already in `workspace_settings` keep parsing and
   * a rollback across D-1109 still finds the policy it was using. **Do not wire a control to it:**
   * an admin who switches "require a code on a new device" on and gets no second factor is worse
   * off than one who was never offered the switch. Removing it from the admin DTO and the web
   * settings page is a small, separate change in two repositories.
   */
  'auth.otpPolicy': { schema: otpPolicySchema, default: 'new_device' },
  /**
   * **INERT since D-1109**, for the same reason as `auth.otpPolicy`: there is no password login to
   * disable. See the note above before building anything on it.
   */
  'auth.passwordLoginEnabled': { schema: z.boolean(), default: true },
  /** Non-empty = invites (non-guest) and SSO auto-join limited to these domains. */
  'auth.allowedEmailDomains': { schema: emailDomainListSchema, default: [] },
  /** Managers may invite members and guests (D-006). */
  'org.managersCanInvite': { schema: z.boolean(), default: false },
  /** Optional default expiry for guest accounts, in days. */
  'org.guestMaxDays': { schema: z.number().int().min(1).max(3650).nullable(), default: null },
  /** Accent colour (D-017); must also pass the contrast rule (`accent_contrast_too_low`). */
  'branding.accentColor': {
    schema: z.string().regex(HEX_COLOR_PATTERN),
    default: DEFAULT_ACCENT_COLOR,
  },
  /** Default theme for members who have not chosen one. */
  'branding.defaultTheme': { schema: themeSchema, default: 'system' },

  // ------------------------------------------------------------ Phase 1 — messaging, files, presence
  /** `@here` / `@channel` asks for confirmation above this many recipients (SPEC §4.3). */
  'messaging.broadcastWarnThreshold': {
    schema: z.number().int().min(1).max(100_000),
    default: 10,
  },
  /** Attachment size limit in bytes (default 1 GiB, SPEC §4.3); multipart above 100 MiB. */
  'messaging.maxUploadBytes': {
    schema: z
      .number()
      .int()
      .min(1024 * 1024)
      .max(20 * 1024 * 1024 * 1024),
    default: 1024 * 1024 * 1024,
  },
  /** Minutes after sending during which the author may edit; null = no limit. */
  'messaging.editWindowMinutes': {
    schema: z.number().int().min(1).max(525_600).nullable(),
    default: null,
  },
  /** Days deleted and pre-edit content stays readable by Owners before the purge (D-046). */
  'messaging.deletedContentRetentionDays': {
    schema: z.number().int().min(1).max(3650),
    default: 90,
  },
  /** Roles that may create channels (guests never can). */
  'messaging.channelCreationRoles': {
    schema: z
      .array(z.enum(['owner', 'admin', 'manager', 'member']))
      .min(1)
      .refine((roles) => new Set(roles).size === roles.length, 'Roles must be unique'),
    default: ['owner', 'admin', 'manager', 'member'],
  },
  /** Minutes without input before a device counts as idle (automatic away, SPEC §4.5). */
  'presence.idleMinutes': { schema: z.number().int().min(1).max(240), default: 10 },
  /** Mobile push delay while the person is active on desktop (SPEC §4.6, D-110). */
  'notifications.pushDelaySeconds': {
    schema: z.union([z.literal(0), z.literal(60), z.literal(120), z.literal(300), z.literal(600)]),
    default: 60,
  },
  /** File extensions refused at upload (`415 file_type_blocked`), lowercase without the dot. */
  'files.blockedExtensions': {
    schema: z.array(z.string().regex(/^[a-z0-9]{1,16}$/)).max(100),
    default: [],
  },

  // ------------------------------------------------------------ Phase 2 (PLAN §3.1 workspace_settings)
  /**
   * SSO providers the workspace allows (D-008). A provider also needs its client id in the
   * environment (`SSO_GOOGLE_CLIENT_ID`, `SSO_MICROSOFT_CLIENT_ID`); without it the button is hidden.
   * No just-in-time sign-up: people still need a membership or a pending invite.
   */
  'auth.ssoProviders': { schema: ssoProvidersSchema, default: [] },
  /** Default N (hours) of the unread-mentions email digest (SPEC §4.6). */
  'notifications.emailDigestDefaultHours': {
    schema: emailDigestDefaultHoursSchema,
    default: 4,
  },
  /** Who may create and edit user groups (`@sales-team`); guests never. */
  'messaging.userGroupsManagedBy': { schema: managedBySchema, default: 'admins' },
  /** Who may upload custom emoji; guests never. */
  'messaging.emojiUploadBy': { schema: managedBySchema, default: 'admins' },
  /** Hostnames never unfurled (a host and its subdomains), lowercase. */
  'messaging.unfurlBlockedDomains': { schema: unfurlBlockedDomainsSchema, default: [] },
  /** Who may manage a channel's incoming webhooks: admins, or any member of the channel. */
  'integrations.webhooksManagedBy': { schema: webhooksManagedBySchema, default: 'admins' },
  /**
   * Owner-only (D-104): RFC 1918 ranges that outgoing webhooks and polling adapters may reach (never
   * unfurls, imports or bot commands). Only effective with `OUTBOUND_ALLOW_PRIVATE_NETWORKS=true`.
   */
  'integrations.webhookAllowedPrivateCidrs': {
    schema: z
      .array(z.string().regex(IPV4_CIDR_PATTERN, 'Use an IPv4 CIDR such as 10.20.0.0/16'))
      .max(20),
    default: [],
  },
  /** Upload ceiling of a Slack export ZIP (`slack_import_archive`), default 20 GiB. */
  'import.maxArchiveBytes': {
    schema: importMaxArchiveBytesSchema,
    default: 20 * 1024 * 1024 * 1024,
  },
  // ------------------------------------------------------------ Phase 4 — task follow-ups (PLAN §3.2)
  // Workspace defaults; `department_task_settings` rows override N, M, the nudge time and the
  // acceptance reminder per department (`resolveTaskSettings` in @orbit/domain/tasks, D-165).
  /** Reminder to the owner of an unaccepted task, in working hours (SPEC §6.2). */
  'tasks.acceptanceReminderWorkingHours': { schema: workingHours, default: 24 },
  /** Then notify the delegator (2 × the reminder by default). */
  'tasks.acceptanceDelegatorNotifyWorkingHours': { schema: workingHours, default: 48 },
  /** Remind the delegator to answer a push-back. */
  'tasks.pushbackReminderWorkingHours': { schema: workingHours, default: 9 },
  /** Remind the delegator to verify a Done task. */
  'tasks.verificationReminderWorkingHours': { schema: workingHours, default: 18 },
  /** Remind the decider this many working hours before a decision deadline. */
  'tasks.decisionDeadlineReminderWorkingHours': { schema: workingHours, default: 4 },
  /** Morning digest DM, and the morning pass for overdue notices and escalations (SPEC §6.7). */
  'tasks.morningDigestTime': { schema: hhmm, default: '09:15' },
  'tasks.dueTodayNudgeTime': { schema: hhmm, default: '16:00' },
  /** Slipping view: `slip_count >=` this … */
  'tasks.slippingSlipCount': { schema: z.number().int().min(1).max(100), default: 2 },
  /** … or overdue by more than this many calendar days (D-059). */
  'tasks.slippingOverdueDays': { schema: z.number().int().min(0).max(365), default: 3 },
  /** N: escalate an overdue delegated task to the delegator after N working days. */
  'tasks.escalateDelegatorAfterOverdueDays': {
    schema: z.number().int().min(1).max(30),
    default: 2,
  },
  /** M (≥ N, checked by the service): escalate to the owner's manager after M working days. */
  'tasks.escalateManagerAfterOverdueDays': { schema: z.number().int().min(1).max(365), default: 5 },
  // ------------------------------------------------------------ Phase 5 — PTSTDM (PLAN §3.3 sec. 4.1)
  // Writers (D-097): `ptstdm.checklistPartialCredit` and `ptstdm.dataFreezeDays` need
  // hr_compensation or the Owner; the other keys follow ptstdm-editor (admin+ workspace-wide).
  /** Start of the financial year (April, D-063) for quarters, halves and years. */
  'ptstdm.fiscalYearStartMonth': { schema: z.number().int().min(1).max(12), default: 4 },
  /** Data freeze at period end + N days (SPEC §7.9 "T+N"). */
  'ptstdm.dataFreezeDays': { schema: z.number().int().min(0).max(60), default: 3 },
  /** A frozen KPI period with no live pay plan closes after N more days. */
  'ptstdm.periodCloseAfterDays': { schema: z.number().int().min(0).max(120), default: 7 },
  /** Projected achievement for "on track" (sec. 13.3). */
  'ptstdm.statusOnTrackMinPct': { schema: z.number().min(0).max(1000), default: 100 },
  /** Projected achievement for "at risk"; below it is "off track". */
  'ptstdm.statusAtRiskMinPct': { schema: z.number().min(0).max(1000), default: 85 },
  /** Credit for a "partially done" checklist item in compliance %. */
  'ptstdm.checklistPartialCredit': { schema: z.number().min(0).max(1), default: 0.5 },
  /** Default spot-check rate for new self-reported KPIs (SPEC §7.6 example). */
  'ptstdm.defaultSpotCheckPct': { schema: z.number().min(0).max(100), default: 10 },
  /** Default self-report vs system variance threshold. */
  'ptstdm.defaultVarianceThresholdPct': { schema: z.number().min(0).max(10_000), default: 10 },
  /** Default skill reassessment cadence in months (quarterly, SPEC §7.3). */
  'ptstdm.skillReassessmentMonths': { schema: z.number().int().min(1).max(24), default: 3 },
  /** Daily checklist generation time (architecture §3.5). */
  'ptstdm.checklistGenerationTime': { schema: hhmm, default: '00:05' },
  /** D-093 (proposed): department heads see the per-person rows of their department. */
  'ptstdm.deptHeadSeesReports': { schema: z.boolean(), default: true },

  // ------------------------------------------------------------ Phase 3 — push and read receipts
  /**
   * What a push shows when the person did not choose (`notification_preferences.push_preview`
   * NULL): sender and text (`full`, default, Q3), the sender only, or nothing (D-505).
   */
  'notifications.pushPreviewDefault': { schema: pushPreviewSchema, default: 'full' },
  /**
   * Where read receipts show (D-550): `off`, `dms` (DMs and group DMs, default) or `all` (plus
   * channels up to 200 members). Owner and Admin change it in Workspace settings.
   */
  'messaging.readReceipts': { schema: readReceiptsSettingSchema, default: 'dms' },

  // ------------------------------------------------------------ Phase 6 — calls and huddles (D-026, D-648)
  /**
   * Calls and huddles are off until an Admin switches them on (and the server has LiveKit
   * configured, `GET /calls/availability`). Off: every call route answers `403 calls_disabled`.
   */
  'calls.enabled': { schema: z.boolean(), default: false },
  /** People in one call or huddle; never above the 50-person hard ceiling (SPEC §5). */
  'calls.maxParticipants': {
    schema: z.number().int().min(2).max(CALL_LIMITS.maxParticipants),
    default: CALL_LIMITS.maxParticipants,
  },
  /** How long a ringing call rings before it ends as missed (PLAN §4.4, D-094). */
  'calls.ringTimeoutSeconds': {
    schema: z
      .number()
      .int()
      .min(CALL_LIMITS.minRingTimeoutSeconds)
      .max(CALL_LIMITS.maxRingTimeoutSeconds),
    default: CALL_LIMITS.ringTimeoutSeconds,
  },

  // ------------------------------------------------------------ Wave 4 — AI, drafts only (SPEC §8, D-025, D-061, D-870)
  /**
   * The workspace switch of every AI feature (default off). The server also needs
   * `ANTHROPIC_API_KEY` (and `AI_ENABLED` not false): without them calls answer `503 ai_unavailable`.
   */
  'ai.enabled': { schema: z.boolean(), default: false },
  /** One flag per feature (`ai.features.<camelCase>`), on by default once `ai.enabled` is on. */
  'ai.features.messageToTask': { schema: z.boolean(), default: true },
  'ai.features.transcriptExtract': { schema: z.boolean(), default: true },
  'ai.features.threadSummary': { schema: z.boolean(), default: true },
  'ai.features.channelCatchup': { schema: z.boolean(), default: true },
  'ai.features.dailyReportSummary': { schema: z.boolean(), default: true },
  'ai.features.weeklyReviewNarrative': { schema: z.boolean(), default: true },
  'ai.features.appraisalComment': { schema: z.boolean(), default: true },

  // ------------------------------------------------------------ Wave 4 — optional antivirus (SPEC §10.3, D-910)
  /** ClamAV scanning of new files (the `clamav` profile and `AV_SCAN_ENABLED` must be on too). */
  'files.antivirusEnabled': { schema: z.boolean(), default: false },

  // ------------------------------------------------------------ Wave 4 — training, weekly review, meetings (D-820, D-840, D-850)
  /** Days before a training due date a reminder DM is sent (the 09:00 `training-reminders` job). */
  'training.reminderLeadDays': {
    schema: z.array(z.number().int().min(0).max(60)).max(6),
    default: [7, 1],
  },
  /** While a mandatory assignment is overdue, a reminder every N days (0 = the first notice only). */
  'training.overdueReminderEveryDays': { schema: z.number().int().min(0).max(30), default: 3 },
  /** Default due date of gap-based assignments, in days. */
  'training.defaultDueDays': { schema: z.number().int().min(1).max(365), default: 30 },
  /** A recertification is assigned this many days before a completion expires. */
  'training.recertificationLeadDays': { schema: z.number().int().min(0).max(365), default: 30 },
  /** The Sunday weekly review job (SPEC §6.8). */
  'weeklyReview.enabled': { schema: z.boolean(), default: true },
  /** Workspace-local time of the Sunday post. */
  'weeklyReview.postTime': { schema: hhmm, default: '20:00' },
  /** Who writes meeting notes: the chair and note taker only, or every attendee. */
  'meetings.notesEditableBy': {
    schema: z.enum(['chair_and_note_taker', 'attendees']),
    default: 'attendees',
  },

  // ------------------------------------------------------------ Wave 5 — calendar, guests, webinars, booking pages (§13.1, D-1048)
  // Admins write every key below (`/admin/settings/calendar`, audited per key); turning a switch off
  // never deletes anything and on again resumes (D-1048).
  /** Outside guests on meetings and their links; off = every guest link answers 404 (D-1011). */
  'calendar.externalGuestsEnabled': { schema: z.boolean(), default: true },
  /** The kill switch of every public guest, webinar and booking route (D-1036). */
  'calendar.publicPagesEnabled': { schema: z.boolean(), default: true },
  /** Guests per general meeting (default and ceiling 50, D-1011). */
  'calendar.maxGuestsPerMeeting': {
    schema: z.number().int().min(1).max(CALENDAR_LIMITS.maxGuestsPerMeeting),
    default: CALENDAR_LIMITS.maxGuestsPerMeeting,
  },
  /** Days a one-off's or an ended series' guest links stay valid after the last end (D-1012). */
  'calendar.guestLinkGraceDays': { schema: z.number().int().min(1).max(60), default: 7 },
  /** Minutes a guest may wait in the lobby (D-1028). */
  'calendar.lobbyWaitMinutes': { schema: z.number().int().min(5).max(120), default: 30 },
  /**
   * Webinar recording (D-1231). Off until an admin turns it on, like `calls.enabled` (D-648):
   * recording a roomful of people is not something a deployment should start doing because it was
   * upgraded. Off also stops an in-progress recording's sweep from starting new ones; it never
   * deletes what was already recorded.
   */
  'calendar.recordingEnabled': { schema: z.boolean(), default: false },
  /** Days a finished webinar recording is kept before the retention sweep removes the file (D-1231). */
  'calendar.recordingRetentionDays': {
    schema: z
      .number()
      .int()
      .min(RECORDING_LIMITS.minRetentionDays)
      .max(RECORDING_LIMITS.maxRetentionDays),
    default: RECORDING_LIMITS.defaultRetentionDays,
  },
  /** Private subscription feeds (D-1023). */
  'calendar.feedsEnabled': { schema: z.boolean(), default: true },
  /** "Starts in N min" reminder DMs, unless a member chose otherwise (D-1015, D-1042). */
  'calendar.defaultReminderMinutes': { schema: calendarReminderMinutesSchema, default: 10 },
  /** Outside people's data is anonymised this many days after the series' end, removal or decision (D-1034). */
  'calendar.guestDataRetentionDays': { schema: z.number().int().min(30).max(730), default: 180 },
  /** Leave in free/busy: "Out of office" (Akshay, 11 Oct 2026) or plain busy; never type or notes (D-1024). */
  'calendar.freeBusyLeaveVisibility': {
    schema: freeBusyLeaveVisibilitySchema,
    default: 'out_of_office',
  },
  /** PTSTDM meetings in feeds: "Busy" or their titles; a 1:1 is always busy (D-1023). */
  'calendar.feedPerformanceDetail': { schema: feedPerformanceDetailSchema, default: 'busy' },
  /** First invitations to invited guests and opted-in members, per workspace per day (D-1022). */
  'calendar.organiserEmailDailyCap': {
    schema: z.number().int().min(0).max(100_000),
    default: 500,
  },
  /** The same, per organiser per day. */
  'calendar.perOrganiserEmailDailyCap': {
    schema: z.number().int().min(0).max(100_000),
    default: 200,
  },
  /** Notices and confirmations caused by public forms, per workspace per day. */
  'calendar.selfServiceEmailDailyCap': {
    schema: z.number().int().min(0).max(100_000),
    default: 300,
  },
  /** First contacts per recipient address per day. */
  'calendar.perRecipientEmailDailyCap': { schema: z.number().int().min(0).max(100), default: 6 },
  /** Webinars (off until an admin turns them on; created by managers and above, D-1031). */
  'webinars.enabled': { schema: z.boolean(), default: false },
  /**
   * Attendees per webinar, 1..100 (Akshay, 11 Oct 2026). The default is 50 until the load check
   * (acceptance A24) measures what the shared VM holds (D-1031).
   */
  'webinars.maxAttendees': {
    schema: z.number().int().min(1).max(CALENDAR_LIMITS.webinarMaxAttendees),
    default: 50,
  },
  /** Booking pages (off until an admin turns them on, D-1035). */
  'bookings.enabled': { schema: z.boolean(), default: false },

  // ------------------------------------------------------------ The daily and monthly digests (D-1207)
  // One DM per person per period, in three sections (tasks, performance, activity). Both are OFF
  // by default: a digest that nobody asked for is the kind of message people mute the sender over,
  // and muting Orbit AI also mutes the task follow-ups, which are not optional.
  /** The end-of-day digest DM. */
  'digests.dailyEnabled': { schema: z.boolean(), default: false },
  /** When it is sent, in each member's own zone (HH:MM). */
  'digests.dailyTime': { schema: hhmm, default: '18:30' },
  /** The month-end digest DM, sent on the first working day of the next month. */
  'digests.monthlyEnabled': { schema: z.boolean(), default: false },
  'digests.monthlyTime': { schema: hhmm, default: '09:30' },

  // ------------------------------------------------------------ Wave 4 — variable pay (SPEC §7.9, D-097; Owner-only writers)
  /** Days an employee can dispute after results are published (`hr_compensation` may also change it). */
  'pay.reviewWindowDays': { schema: z.number().int().min(1).max(30), default: 3 },
  /**
   * Owner approval is needed when a person's period total, a payroll line or the period or export
   * total is above this many paise (D-105); default ₹50,000.
   */
  'pay.ownerApprovalThresholdPaise': {
    schema: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    default: 5_000_000,
  },
  /** Whether managers see payout amounts of their reports: `hidden`, `direct_reports`, `reporting_tree`. */
  'pay.managerPayoutVisibility': { schema: managerPayoutVisibilitySchema, default: 'hidden' },
  'pay.payrollExportFormat': { schema: z.enum(['csv', 'xlsx']), default: 'csv' },
  /** Days an encrypted payroll file is kept before it is purged (it can be regenerated, D-066). */
  'pay.exportFileRetentionDays': { schema: z.number().int().min(1).max(365), default: 30 },
} as const satisfies Record<string, { schema: z.ZodType; default: unknown }>;

/** The `ai.features.*` key of each AI feature. */
export const AI_FEATURE_SETTING_KEYS = {
  message_to_task: 'ai.features.messageToTask',
  transcript_extract: 'ai.features.transcriptExtract',
  thread_summary: 'ai.features.threadSummary',
  channel_catchup: 'ai.features.channelCatchup',
  daily_report_summary: 'ai.features.dailyReportSummary',
  weekly_review_narrative: 'ai.features.weeklyReviewNarrative',
  appraisal_comment: 'ai.features.appraisalComment',
} as const satisfies Record<AiFeature, keyof typeof SETTINGS_REGISTRY>;

export type SettingKey = keyof typeof SETTINGS_REGISTRY;
export type SettingValue<K extends SettingKey> = z.output<(typeof SETTINGS_REGISTRY)[K]['schema']>;
/** Every registered setting with its effective value (stored or default). */
export type WorkspaceSettings = { [K in SettingKey]: SettingValue<K> };

export const SETTING_KEYS = Object.keys(SETTINGS_REGISTRY) as SettingKey[];

/** Same pattern as the `workspace_settings.key` CHECK constraint. */
export const SETTING_KEY_PATTERN = /^[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)+$/;

export function isSettingKey(key: string): key is SettingKey {
  return Object.hasOwn(SETTINGS_REGISTRY, key);
}

/** A fresh copy of the defaults (arrays are not shared between callers). */
export function defaultWorkspaceSettings(): WorkspaceSettings {
  return Object.fromEntries(
    SETTING_KEYS.map((key) => [key, JSON.parse(JSON.stringify(SETTINGS_REGISTRY[key].default))]),
  ) as WorkspaceSettings;
}

export type SettingParseResult<K extends SettingKey> =
  { ok: true; value: SettingValue<K> } | { ok: false; error: z.ZodError };

/** Validates a value for a key with the key's schema. */
export function parseSettingValue<K extends SettingKey>(
  key: K,
  value: unknown,
): SettingParseResult<K> {
  const result = SETTINGS_REGISTRY[key].schema.safeParse(value);
  return result.success
    ? { ok: true, value: result.data as SettingValue<K> }
    : { ok: false, error: result.error };
}

/**
 * Merges stored rows over the defaults. Rows with unknown keys or values that no longer pass the
 * schema (e.g. after a schema tightened) fall back to the default instead of breaking reads.
 */
export function resolveWorkspaceSettings(
  rows: Iterable<{ key: string; value: unknown }>,
): WorkspaceSettings {
  const settings = defaultWorkspaceSettings();
  const target = settings as Record<SettingKey, unknown>;
  for (const row of rows) {
    if (!isSettingKey(row.key)) continue;
    const parsed = parseSettingValue(row.key, row.value);
    if (parsed.ok) target[row.key] = parsed.value;
  }
  return settings;
}

// ---------------------------------------------------------------- who may write which key (D-097)

/**
 * Who may write a key: roles, and grants (`member_grants`) that also qualify. A key not listed
 * here is written by Owner and Admin. The `pay.*` keys are Owner-only, except `pay.reviewWindowDays`,
 * which `hr_compensation` holders may also change; the pay-affecting PTSTDM keys need
 * `hr_compensation` or the Owner; the private-range allowlist is Owner-only (D-104). The module
 * settings routes refuse anyone else with `403 setting_not_writable`.
 */
export interface SettingWriter {
  roles: readonly Role[];
  grants?: readonly MemberGrant[];
}

export const DEFAULT_SETTING_WRITER = {
  roles: ['owner', 'admin'],
} as const satisfies SettingWriter;

const OWNER_ONLY_WRITER = { roles: ['owner'] } as const satisfies SettingWriter;
const OWNER_OR_HR_WRITER = {
  roles: ['owner'],
  grants: ['hr_compensation'],
} as const satisfies SettingWriter;

export const SETTING_WRITERS: Readonly<Partial<Record<SettingKey, SettingWriter>>> = Object.freeze({
  'pay.reviewWindowDays': OWNER_OR_HR_WRITER,
  'pay.ownerApprovalThresholdPaise': OWNER_ONLY_WRITER,
  'pay.managerPayoutVisibility': OWNER_ONLY_WRITER,
  'pay.payrollExportFormat': OWNER_ONLY_WRITER,
  'pay.exportFileRetentionDays': OWNER_ONLY_WRITER,
  'ptstdm.checklistPartialCredit': OWNER_OR_HR_WRITER,
  'ptstdm.dataFreezeDays': OWNER_OR_HR_WRITER,
  'integrations.webhookAllowedPrivateCidrs': OWNER_ONLY_WRITER,
});

export function settingWriterOf(key: SettingKey): SettingWriter {
  return SETTING_WRITERS[key] ?? DEFAULT_SETTING_WRITER;
}

/** Whether a member with this role and these grants may write `key`. */
export function canWriteSetting(
  viewer: { role: Role; grants: readonly MemberGrant[] },
  key: SettingKey,
): boolean {
  const writer = settingWriterOf(key);
  if (writer.roles.includes(viewer.role)) return true;
  return viewer.role !== 'guest' && (writer.grants ?? []).some((g) => viewer.grants.includes(g));
}

/**
 * Keys whose change also writes `pay_audit_events` (D-097): every `pay.*` key and the pay-affecting
 * PTSTDM keys.
 */
export function isPayAffectingSetting(key: SettingKey): boolean {
  return (
    key.startsWith('pay.') ||
    key === 'ptstdm.checklistPartialCredit' ||
    key === 'ptstdm.dataFreezeDays'
  );
}
